import { createRouter, createWebHistory, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import DashboardView from '../views/DashboardView.vue'
import store from '../store'
import { useAdmin } from '../composables/useAdmin'
import { openInFocusMode } from '../composables/useFocusModePreference'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    name: 'dashboard',
    component: DashboardView,
  },
  {
    path: '/foco',
    name: 'foco',
    component: () => import('../views/FocoView.vue'),
    meta: { permKey: 'foco.view' },
  },
  {
    path: '/templates',
    name: 'templates',
    component: () => import('../views/TemplatesView.vue'),
    meta: { permKey: 'templates.view' },
  },
  {
    path: '/resumo',
    name: 'resumo',
    component: () => import('../views/ResumoView.vue'),
    meta: { permKey: 'resumo.view' },
  },
  {
    path: '/relatorio-resolucao',
    name: 'relatorio-resolucao',
    component: () => import('../views/RelatorioResolucaoView.vue'),
    meta: { permKey: 'relatorioResolucao.view' },
  },
  {
    path: '/contacts',
    name: 'contacts',
    component: () => import('../views/ContactsView.vue'),
    meta: { permKey: 'contacts.view' },
  },
  {
    path: '/sla',
    name: 'sla',
    component: () => import('../views/SLAView.vue'),
    meta: { permKey: 'sla.view' },
  },
  {
    path: '/teams',
    name: 'teams',
    component: () => import('../views/TeamsView.vue'),
    meta: { permKey: 'teams.view' },
  },
  {
    path: '/macros',
    name: 'macros',
    component: () => import('../views/MacrosView.vue'),
    meta: { permKey: 'macros.view' },
  },
  {
    path: '/etiquetas',
    name: 'etiquetas',
    component: () => import('../views/LabelsView.vue'),
    meta: { permKey: 'etiquetas.view' },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminView.vue'),
    meta: { permKey: 'admin.view' },
  },
  {
    path: '/sla-metro',
    name: 'sla-metro',
    component: () => import('../views/SLAMetroView.vue'),
    meta: { permKey: 'slaMetro.view' },
  },
  {
    path: '/avaliacoes',
    name: 'avaliacoes',
    component: () => import('../views/AvaliacoesView.vue'),
    meta: { permKey: 'avaliacoes.view' },
  },
  {
    path: '/fila',
    name: 'fila',
    component: () => import('../views/FilaView.vue'),
    meta: { permKey: 'fila.view' },
  },
  {
    path: '/agentes',
    name: 'agentes',
    component: () => import('../views/AgentesView.vue'),
    meta: { permKey: 'agentes.view' },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
  },
]

const router = createRouter({
  history: process.env.IS_ELECTRON
    ? createWebHashHistory()
    : createWebHistory(process.env.BASE_URL),
  routes,
})

// "Abrir em Modo Foco" (preferência salva no Perfil, ver useFocusModePreference.ts):
// landing padrão do login e da rota "/" vira /foco em vez de /, pra não disparar
// o motor pesado do Dashboard (useDashboardEngine, que busca conversas de todos
// os agentes/times) quando o usuário só quer ver as próprias conversas.
function landingRouteName(): 'foco' | 'dashboard' {
  if (!openInFocusMode.value) return 'dashboard'
  const userId  = store.getters.authUser?.id ?? 0
  const teamIds = (store.getters.userTeamIds ?? []) as number[]
  const { hasPermission } = useAdmin()
  return userId && hasPermission(userId, teamIds, 'foco.view') ? 'foco' : 'dashboard'
}

router.beforeEach((to, _from, next) => {
  const loggedIn = store.getters.isLoggedIn

  if (!to.meta.public && !loggedIn) {
    next({ name: 'login' })
    return
  }
  if (to.name === 'login' && loggedIn) {
    next({ name: landingRouteName() })
    return
  }
  if (to.name === 'dashboard' && loggedIn) {
    const landing = landingRouteName()
    if (landing !== 'dashboard') {
      next({ name: landing })
      return
    }
  }

  // Page-level permission check
  const permKey = to.meta.permKey as string | undefined
  if (permKey && loggedIn) {
    const userId  = store.getters.authUser?.id ?? 0
    const teamIds = (store.getters.userTeamIds ?? []) as number[]
    const { hasPermission } = useAdmin()
    if (userId && !hasPermission(userId, teamIds, permKey)) {
      next({ name: 'dashboard' })
      return
    }
  }

  next()
})

export default router
